'use client';

import { useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  addEdge as rfAddEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Connection,
  Edge,
  Node,
  NodeChange,
  EdgeChange,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FlowArrow } from '@phosphor-icons/react';
import { useWorkflowStore } from '../../stores/workflowStore';
import { useUploadedFileStore } from '../../stores/uploadedFileStore';
import { OperatorNode } from './nodes/OperatorNode';
import { NODE_META, getNodeColor } from './nodeTypes';

const INPUT_NODE_TYPES = new Set(['load_excel', 'load_csv', 'load_api']);

// All workflow node types map to the same custom renderer
const NODE_TYPES = Object.fromEntries(
  Object.keys(NODE_META).map((k) => [k, OperatorNode])
) as Record<string, typeof OperatorNode>;

// ReactFlow edge style defaults — vertical top-to-bottom flow
const EDGE_DEFAULTS = {
  style: { stroke: '#a1a1aa', strokeWidth: 2 },
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#a1a1aa', width: 14, height: 14 },
};

export function WorkflowCanvas() {
  const {
    nodes: storeNodes,
    edges: storeEdges,
    setSelectedNode,
    addEdge: addStoreEdge,
    addNode: addStoreNode,
    updateNode,
    removeNode,
    removeEdge,
  } = useWorkflowStore();

  const { clearFileForNode } = useUploadedFileStore();

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { project } = useReactFlow();

  // Local RF state — the canvas is the visual layer; store is the source of truth
  const [rfNodes, setRfNodes] = useNodesState([]);
  const [rfEdges, setRfEdges] = useEdgesState([]);

  // ── Sync store → canvas ───────────────────────────────────────────────────
  useEffect(() => {
    setRfNodes(
      storeNodes.map((n) => ({
        id: n.id,
        type: n.type in NODE_TYPES ? n.type : 'filter', // fallback
        position: n.position,
        data: n.data,
        selected: false,
      }))
    );
  }, [storeNodes, setRfNodes]);

  useEffect(() => {
    setRfEdges(
      storeEdges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        ...EDGE_DEFAULTS,
      }))
    );
  }, [storeEdges, setRfEdges]);

  useEffect(() => {
    const attr = document.querySelector<HTMLElement>('.react-flow__attribution');
    if (attr) attr.style.display = 'none';
  }, [])

  // ── Node / Edge change handlers ───────────────────────────────────────────
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setRfNodes((nds) => applyNodeChanges(changes, nds));
      // Sync removes to store; also clean up uploaded files for input nodes
      changes
        .filter((c) => c.type === 'remove')
        .forEach((c) => {
          const id = (c as { id: string }).id;
          const node = storeNodes.find((n) => n.id === id);
          if (node && INPUT_NODE_TYPES.has(node.type ?? '')) {
            clearFileForNode(id);
          }
          removeNode(id);
        });
    },
    [setRfNodes, removeNode, clearFileForNode, storeNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setRfEdges((eds) => applyEdgeChanges(changes, eds));
      changes
        .filter((c) => c.type === 'remove')
        .forEach((c) => removeEdge((c as { id: string }).id));
    },
    [setRfEdges, removeEdge]
  );

  // Sync drag-stop position back to store
  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      updateNode(node.id, { position: node.position });
    },
    [updateNode]
  );

  // ── Connect ───────────────────────────────────────────────────────────────
  const onConnect = useCallback(
    (connection: Connection) => {
      const edgeId = `e_${connection.source}_${connection.target}_${Date.now()}`;
      const newEdge: Edge = {
        id: edgeId,
        source: connection.source!,
        target: connection.target!,
        ...EDGE_DEFAULTS,
      };
      setRfEdges((eds) => rfAddEdge(newEdge, eds));
      addStoreEdge({ id: edgeId, source: connection.source!, target: connection.target! });
    },
    [setRfEdges, addStoreEdge]
  );

  // ── Node selection ────────────────────────────────────────────────────────
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const storeNode = storeNodes.find((n) => n.id === node.id);
      if (storeNode) setSelectedNode(storeNode);
    },
    [storeNodes, setSelectedNode]
  );

  const onPaneClick = useCallback(() => setSelectedNode(null), [setSelectedNode]);

  // ── Drag-and-drop from NodePanel ─────────────────────────────────────────
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  /** Sink-only nodes: no outgoing connections. */
  const OUTPUT_TYPES = new Set(['export_excel', 'export_csv']);

  /**
   * Find the single connectable "tail" node:
   * - has no outgoing edges, AND
   * - is not an output-only node (those have no bottom/source handle)
   */
  const findTailNode = useCallback(() => {
    if (storeNodes.length === 0) return null;
    const sourcesWithEdge = new Set(storeEdges.map((e) => e.source));
    const sinks = storeNodes.filter(
      (n) => !sourcesWithEdge.has(n.id) && !OUTPUT_TYPES.has(n.type ?? '')
    );
    return sinks.length === 1 ? sinks[0] : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNodes, storeEdges]);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const nodeType = e.dataTransfer.getData('application/workflow-node-type');
      if (!nodeType) return;

      const meta = NODE_META[nodeType];
      if (!meta) return;

      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      const dropPosition = project({
        x: e.clientX - (bounds?.left ?? 0),
        y: e.clientY - (bounds?.top ?? 0),
      });

      // Find the last node with no outgoing edge
      const tailNode = findTailNode();

      // If there's a clear tail, snap the new node below it for a tidy layout;
      // otherwise place it at the drop position.
      const position = tailNode
        ? { x: tailNode.position.x, y: tailNode.position.y + 150 }
        : dropPosition;

      const id = `${nodeType}_${Date.now()}`;
      addStoreNode({
        id,
        type: nodeType,
        position,
        data: {
          label: meta.label,
          config: { type: nodeType, params: {} },
          status: 'idle',
        },
      });

      // Auto-wire: only connect if the new node can accept an incoming edge
      // (input-only nodes like load_excel have no top/target handle)
      if (tailNode && !INPUT_NODE_TYPES.has(nodeType)) {
        const edgeId = `e_${tailNode.id}_${id}`;
        addStoreEdge({ id: edgeId, source: tailNode.id, target: id });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, addStoreNode, addStoreEdge, findTailNode]
  );

  const isEmpty = storeNodes.length === 0;

  return (
    <div ref={reactFlowWrapper} className="flex-1 h-full min-w-0">
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodeDragStop={onNodeDragStop}
        onDragOver={onDragOver}
        onDrop={onDrop}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.35, maxZoom: 1.2 }}
        deleteKeyCode={['Delete', 'Backspace']}
        attributionPosition="bottom-right"
        className="bg-zinc-50"
        defaultEdgeOptions={EDGE_DEFAULTS}
        proOptions={{ hideAttribution: false }}
      >
        {/* Dot-grid background */}
        <Background
          variant={BackgroundVariant.Dots}
          gap={24}
          size={1}
          color="#d4d4d8"
        />

        {/* Controls */}
        <Controls
          className="!shadow-none !border !border-zinc-200 !rounded-xl overflow-hidden"
          showFitView
          showInteractive
        />

        {/* MiniMap */}
        <MiniMap
          nodeStrokeWidth={2}
          zoomable
          pannable
          className="!border !border-zinc-200 !rounded-xl !shadow-none"
          nodeColor={(node) => getNodeColor(node.data?.config?.type ?? node.type)}
        />

        {/* Empty state */}
        {isEmpty && (
          <Panel position="top-center">
            <div className="mt-12 flex flex-col items-center gap-3 pointer-events-none select-none">
              <div className="w-14 h-14 rounded-2xl bg-white border border-zinc-200 shadow-sm flex items-center justify-center">
                <FlowArrow className="w-7 h-7 text-zinc-300" weight="duotone" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-zinc-400">画布为空</p>
                <p className="text-xs text-zinc-400 mt-0.5">
                  从左侧拖拽节点到此处，或悬停节点后点击 + 快速添加
                </p>
              </div>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}
